import React, { useState } from 'react';
import './LoginPage.css';
import logo from '../../logo.svg';
import { useAuth } from '../../context/AuthContext';
import Snackbar from '../../components/Snackbar';
import { User, Lock, Loader2 } from 'lucide-react';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('');
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setSnackbarMessage('');
    setSnackbarType('');
    try {
      await login(username, password);
      setSnackbarMessage('Login successful!');
      setSnackbarType('success');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Invalid username or password.');
        setSnackbarMessage('Invalid username or password.');
        setSnackbarType('error');
      } else {
        setError('Login failed. Please try again later.');
        setSnackbarMessage('Login failed. Please try again later.');
        setSnackbarType('error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 font-sans">
      
      {/* Left side - Image & Branding */}
      <div className="relative w-full md:w-1/2 h-64 md:h-auto bg-blue-900 flex-shrink-0 shadow-2xl z-10">
        <div className="absolute inset-0 bg-blue-900/40 z-10 mix-blend-multiply"></div>
        <img 
          src="/background.jpg" 
          alt="Melaka Background" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 flex flex-col justify-center items-center md:items-start p-8 md:p-16 h-full text-white text-center md:text-left bg-gradient-to-t md:bg-gradient-to-r from-blue-900/90 to-transparent">
          <div className="md:max-w-2xl w-full">
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 drop-shadow-md leading-tight">
              MAJLIS BANDARAYA MELAKA BERSEJARAH
            </h1>
            <p className="text-lg md:text-2xl text-blue-100 font-medium drop-shadow-sm">
              Sistem Pemantauan Operasi Pam (SPOP 2.0)
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 relative">
        <div className="w-full max-w-md bg-white rounded-[2rem] shadow-xl border border-gray-100 p-8 md:p-12 transform transition-all">
          
          <div className="flex flex-col items-center mb-10">
            <div className="bg-blue-50 p-4 rounded-full mb-4 shadow-sm border border-blue-100">
              <img src={logo} className="h-20 w-auto" alt="MBMB Logo" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">Welcome Back</h2>
            <p className="text-gray-500 mt-2 text-sm">Please sign in to your account</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700 ml-1 block" htmlFor="username">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all outline-none"
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700 ml-1 block" htmlFor="password">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all outline-none"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all transform hover:-translate-y-0.5 mt-8"
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : (
                'SIGN IN'
              )}
            </button>
            
          </form>

        </div>
        
        {/* Footer text */}
        <div className="absolute bottom-6 text-center w-full text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Majlis Bandaraya Melaka Bersejarah. All rights reserved.
        </div>
      </div>
      
      <Snackbar message={snackbarMessage} type={snackbarType} />
    </div>
  );
}

export default LoginPage;
